import React from 'react';
import { Button, ButtonV2, CopyButton, Tooltip } from '@asigna/kit';
import cx from 'classnames';
import { SafeAvatar } from 'components';
import { usePermissionWarning, useReadOnlyWarning } from 'hooks';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import LoadGif from 'images/loading-gif.gif';
import ReadOnlyIcon from 'images/readOnlyIcon.svg';
import VerifyIcon from 'images/verify_green.svg';
import { useDeletePendingDialog, useSendStxDialog } from 'store';
import { IClaimMultiSignWallet, IMultiSignWallet } from 'types';
import { isPendingSafe } from 'utils/helper';

import { arrowsIcon, qrIcon, sendIcon } from './util/images';

type SelectedSafeCardProps = {
  safe: IClaimMultiSignWallet | IMultiSignWallet;
  handleDropdownClick: () => void;
  handleReceiveButton: () => void;
  handleMobileMenuOpen?: (val: boolean) => void;
};

export const SelectedSafeCard: React.FC<SelectedSafeCardProps> = ({
  safe,
  handleDropdownClick,
  handleReceiveButton,
  handleMobileMenuOpen,
}) => {
  const { setSendStxDialogOpen } = useSendStxDialog();
  const isPending = isPendingSafe(safe);
  const { totalBalance } = useSafe();

  const { setDeletePendingDialogOpen } = useDeletePendingDialog();
  const readOnlyWarning = useReadOnlyWarning();
  const permissionWarning = usePermissionWarning('PROPOSE');

  const { _id, address, name, owners } = safe;
  const threshold = safe.threshold;
  const [network] = useNetwork();

  const verified = isPending ? safe.owners.filter((owner) => owner.verified).length : 0;

  const safeFiatBalance = '$' + totalBalance.toFixed(2);

  const SendButton = (
    <Button
      leftIcon={sendIcon}
      colorType="inherit"
      className="h-36 w-[78px] !rounded-[6px] border-[.5px] border-white-10 bg-white-3 hover:bg-white-10 hover:border-black-4 transition-all"
      size="xs"
      message="Send"
      fontSize="r13"
      disabled={isPending || Boolean(readOnlyWarning) || Boolean(permissionWarning)}
      onClick={() => {
        handleMobileMenuOpen && handleMobileMenuOpen(false);
        setSendStxDialogOpen(true);
      }}
    />
  );

  const handleVerify = () => {
    const searchParams = new URLSearchParams();

    const publicKeys = owners.map((owner) => {
      if ('publicKey' in owner) {
        return owner.publicKey;
      }

      return null;
    });

    searchParams.append('publicKeys', encodeURIComponent(JSON.stringify(publicKeys)));
    searchParams.append('network', network);
    searchParams.append('address', address);
    searchParams.append('threshold', threshold.toString());
    searchParams.append('chain', 'STX');
    window.open('https://verify-address.dev.asigna.io/?' + searchParams.toString());
  };

  return (
    <div
      className={cx(
        'flex flex-col w-full p-16 gap-16 rounded-[12px]',
        Boolean(address)
          ? 'bg-[linear-gradient(119deg,_#211836_0%,_#11111D_70.52%)]'
          : 'bg-[linear-gradient(119deg,_#353535_0%,_#19191A_70.52%)]'
      )}
    >
      <div className="flex items-start gap-8 w-full">
        <div className="relative">
          <div className="flex items-center justify-center rounded-[50%] bg-black-1 w-40 h-40 border border-[.5px] border-primary-20 bg-primary-10">
            <SafeAvatar size="32px" address={_id} colorized={Boolean(address)} />
          </div>
        </div>
        <div className="flex flex-col gap-4 items-start">
          <p className="text-r12 opacity-80 max-w-[100px] text-ellipsis overflow-hidden">{name}</p>
          {!isPending ? (
            <p className="text-m13 text-regular-fa">{safeFiatBalance}</p>
          ) : (
            <p className="text-r13">
              Threshold {threshold}/{owners.length}
            </p>
          )}
        </div>
        <button className="flex items-center ml-[-4px]" onClick={handleVerify}>
          <img src={VerifyIcon} width={20} height={20} alt="" />
        </button>
        <button
          className="w-32 h-32 flex items-center justify-center border rounded-[6px] border-[.5px] border-white-10 bg-white-5 hover:bg-white-10 ml-auto"
          onClick={handleDropdownClick}
        >
          <img src={arrowsIcon} width={20} height={20} alt="arrow" />
        </button>
      </div>
      {!isPending ? (
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-36 h-36 border rounded-[6px] border-[.5px] border-white-10 bg-white-3">
            <p className="text-m13 text-white">
              {threshold}/{owners.length}
            </p>
          </div>
          <Button
            leftIcon={qrIcon}
            iconSize={20}
            colorType="inherit"
            className="w-36 h-36 border !rounded-[6px] border-[.5px] border-white-10 bg-white-3 !p-0 hover:bg-white-10 hover:border-black-4"
            size="sm"
            onClick={handleReceiveButton}
          />

          <CopyButton
            copy={address}
            className="flex items-center justify-center w-36 h-36 border rounded-[6px] border-[.5px] border-white-10 bg-white-3 hover:bg-white-10 hover:border-black-4"
          />

          {Boolean(readOnlyWarning) || Boolean(permissionWarning) ? (
            <Tooltip
              strategy="fixed"
              position="bottom-end"
              content={readOnlyWarning || permissionWarning}
              className="max-w-[200px]"
            >
              {SendButton}
            </Tooltip>
          ) : (
            SendButton
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-around gap-4">
            <div className="bg-white-3 rounded-[6px] border-[.5px] border-black-4 flex items-center w-96 h-36 justify-center gap-4">
              <div className="w-4 h-4 rounded-[50%] bg-success" />
              <p className="text-r12 text-success">{verified} Joined</p>
            </div>
            <div className="bg-white-3 rounded-[6px] border-[.5px] border-black-4 flex items-center w-96 h-36 justify-center gap-4">
              <span className="w-4 h-4 rounded-[50%] bg-regular" />
              <p className="text-r12 text-white">{owners.length - verified} Pending</p>
            </div>
          </div>
          <ButtonV2
            onClick={() => {
              setDeletePendingDialogOpen(_id);
            }}
            color="secondary"
            size="sm"
            className="min-w-[100px]"
          >
            Delete
          </ButtonV2>
        </div>
      )}
      {isPending && (
        <>
          <div className="h-[1px] bg-white-10 mx-[-16px]" />
          <div className="flex items-center gap-8 justify-center">
            <img src={LoadGif} width={16} height={16} alt="loading" />
            <p className="text-m12 text-regular">Waiting owners to join</p>
          </div>
        </>
      )}
      {Boolean(readOnlyWarning) && (
        <div className="flex items-center gap-[6px] text-r14 text-regular rounded-[24px] bg-white-10 px-[10px] py-[4px] w-fit mx-auto">
          <img src={ReadOnlyIcon} width={16} height={16} alt="" />
          <span>Read only</span>
        </div>
      )}
    </div>
  );
};
